import React, { useEffect, useState, useRef, Suspense } from 'react';
import { motion, useAnimation, useScroll, useTransform } from 'framer-motion';
import * as THREE from 'three';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';

// Three.js Component for luxury 3D background effect
const ThreeBackground = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();

    // Camera setup with better perspective for luxury feel
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 5;

    // Renderer with premium settings
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio > 1 ? 2 : 1, 2)); // Premium rendering with performance limit
    mountRef.current.appendChild(renderer.domElement);

    // Amber color for all lights - using amber-600 equivalent (#d97706)
    // Ambient light for soft illumination
    const ambientLight = new THREE.AmbientLight(0xd97706, 0.5);
    scene.add(ambientLight);

    // Directional light for dramatic shadows
    const directionalLight = new THREE.DirectionalLight(0xd97706, 0.8);
    directionalLight.position.set(10, 10, 10);
    scene.add(directionalLight);

    // Point light for gleaming effects
    const pointLight = new THREE.PointLight(0xd97706, 1);
    pointLight.position.set(-10, 0, 10);
    scene.add(pointLight);

    // Adjust particle count based on device capability
    const isMobile = window.innerWidth < 768;
    const particlesCount = isMobile ? 300 : 1000;

    // Premium particles
    const particlesGeometry = new THREE.BufferGeometry();
    const positionArray = new Float32Array(particlesCount * 3);
    const scaleArray = new Float32Array(particlesCount);

    for (let i = 0; i < particlesCount; i++) {
      // Position particles in a sphere for premium effect
      const i3 = i * 3;
      const radius = Math.random() * 10 + 5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      positionArray[i3] = radius * Math.sin(phi) * Math.cos(theta);
      positionArray[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positionArray[i3 + 2] = radius * Math.cos(phi);

      scaleArray[i] = Math.random();
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3));
    particlesGeometry.setAttribute('scale', new THREE.BufferAttribute(scaleArray, 1));

    // Use custom shaders for more sophisticated particle effect - using amber-600 equivalent
    const particleMaterial = new THREE.ShaderMaterial({
      vertexShader: `
        attribute float scale;
        varying vec3 vColor;
        
        void main() {
          vColor = mix(vec3(0.85, 0.47, 0.02), vec3(0.71, 0.47, 0.02), scale); // Amber color variations
          
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = scale * 4.0 * (300.0 / -mvPosition.z);
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          
          float alpha = 1.0 - (dist * 2.0);
          gl_FragColor = vec4(vColor, alpha * 0.7);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(particlesGeometry, particleMaterial);
    scene.add(particles);

    // Scale geometry based on device size
    const geometryScale = isMobile ? 0.7 : 1;

    // Luxury sacred geometry - Platonic solid - using amber-600 equivalent
    const icosahedronGeometry = new THREE.IcosahedronGeometry(2 * geometryScale, 0);
    const icosahedronMaterial = new THREE.MeshPhongMaterial({
      color: 0xd97706,
      transparent: true,
      opacity: 0.1,
      wireframe: true,
      shininess: 100
    });
    const icosahedron = new THREE.Mesh(icosahedronGeometry, icosahedronMaterial);
    scene.add(icosahedron);

    // Second sacred geometry element - Dodecahedron - using amber-200 equivalent
    const dodecahedronGeometry = new THREE.DodecahedronGeometry(3 * geometryScale, 0);
    const dodecahedronMaterial = new THREE.MeshPhongMaterial({
      color: 0xfcd34d,
      transparent: true,
      opacity: 0.05,
      wireframe: true
    });
    const dodecahedron = new THREE.Mesh(dodecahedronGeometry, dodecahedronMaterial);
    scene.add(dodecahedron);

    // Animation loop with performance optimization
    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);

      // Reduce rotation speed on mobile for better performance
      const rotationSpeed = isMobile ? 0.5 : 1;

      particles.rotation.x += 0.0005 * rotationSpeed;
      particles.rotation.y += 0.0003 * rotationSpeed;

      icosahedron.rotation.x += 0.001 * rotationSpeed;
      icosahedron.rotation.y += 0.002 * rotationSpeed;

      dodecahedron.rotation.x -= 0.001 * rotationSpeed;
      dodecahedron.rotation.y -= 0.001 * rotationSpeed;

      // Pulsating effect for premium feel
      const pulseFactor = Math.sin(Date.now() * 0.001) * 0.05 + 1;
      icosahedron.scale.set(pulseFactor, pulseFactor, pulseFactor);

      renderer.render(scene, camera);
    };
    animate();

    // Window resize handler for responsive design
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);

      // Update particle count on resize
      const newIsMobile = window.innerWidth < 768;
      if (newIsMobile !== isMobile) {
        // This would require recreating the particles, which is complex in this context
        // For simplicity, we'll just adjust the scale
        const scale = newIsMobile ? 0.7 : 1;
        particles.scale.set(scale, scale, scale);
        icosahedron.scale.set(scale, scale, scale);
        dodecahedron.scale.set(scale, scale, scale);
      }
    };

    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameId);
      mountRef.current?.removeChild(renderer.domElement);

      // Dispose Three.js resources
      particles.geometry.dispose();
      particles.material.dispose();
      icosahedron.geometry.dispose();
      icosahedron.material.dispose();
      dodecahedron.geometry.dispose();
      dodecahedron.material.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0 z-0" />;
};

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controls = useAnimation();
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced parallax effect
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 0.3], [0, -50]);

  useEffect(() => {
    // Initialize AOS with refined settings
    AOS.init({
      duration: 1400,
      once: false,
      mirror: true,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
      anchorPlacement: 'top-bottom',
      // Disable AOS animations on mobile for better performance
      disable: window.innerWidth < 450
    });

    // Premium animation sequence with improved timings
    const sequence = async () => {
      await controls.start("visible");
      setIsLoaded(true);
    };

    sequence();

    // Cleanup
    return () => {
      AOS.refresh();
    };
  }, [controls]);

  // Enhanced text animation variants
  const textVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: isMobile ? 0.5 + i * 0.2 : 0.9 + i * 0.3, // Faster animations on mobile
        duration: isMobile ? 1 : 1.4,
        ease: [0.2, 0.13, 0.23, 0.96] // Custom premium cubic-bezier
      }
    })
  };

  // Reduce number of particles on mobile
  const particleCount = isMobile ? 8 : 20;

  // Premium amber particles
  const particles = Array.from({ length: particleCount }).map((_, i) => (
    <motion.div
      key={i}
      initial={{
        x: Math.random() * 120 - 60,
        y: Math.random() * 120 - 60,
        opacity: 0
      }}
      animate={{
        x: Math.random() * 240 - 120,
        y: Math.random() * 240 - 120,
        opacity: [0, 0.4, 0],
        scale: [0.6, 1.1, 0.6]
      }}
      transition={{
        duration: 10 + Math.random() * 15,
        repeat: Infinity,
        repeatType: "reverse"
      }}
      className="absolute w-1 h-1 rounded-full"
      style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        background: `radial-gradient(circle, rgba(217,119,6,0.9) 0%, rgba(217,119,6,0) 70%)`
      }}
    />
  ));

  return (
    <div ref={containerRef} className="relative w-full h-screen overflow-hidden bg-gray-950">
      {/* Three.js luxury background - conditionally rendered on non-mobile devices */}


      {/* Enhanced premium particle effect with amber color */}
      <div className="absolute inset-0 z-10 opacity-70">
        {particles}
      </div>

      {/* Background video with premium amber overlay gradient */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ y: backgroundY }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute w-full h-full object-cover scale-110"
          poster="/placeholder-bg.jpg" // Add a placeholder image for when video doesn't load
        >
          <source src="https://ik.imagekit.io/tdlebsr5e/devbhoomigroupcompanies.mp4?updatedAt=1747491869383" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-750 via-gray-800/80 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-amber-900/10 via-black/60 to-black/70"></div>
      </motion.div>

      {/* Luxury geometric elements with amber theming - simplified on mobile */}
      <div className="absolute inset-0 z-10">
        {/* Premium corner accents - adjusted for mobile */}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-8 w-1/4 h-1/4 border-l-2 border-t-2 border-amber-600 opacity-40"></div>
        <div className="absolute right-4 sm:right-8 bottom-4 sm:bottom-8 w-1/4 h-1/4 border-r-2 border-b-2 border-amber-600 opacity-40"></div>

        {/* Diamond pattern overlay with amber coloring */}
        {/* <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #d97706 0, #d97706 1px, transparent 0, transparent 50px)',
            backgroundSize: '70px 70px'
          }}></div>
        </div> */}

        {/* Animated luxury accents with amber coloring - conditionally rendered on non-mobile */}
        {!isMobile && (
          <>
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
              animate={{ opacity: 0.15, scale: 1, rotate: 360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              className="absolute right-1/4 top-1/4 w-72 h-72 border border-amber-600 rounded-full"
            ></motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
              animate={{ opacity: 0.15, scale: 1, rotate: -360 }}
              transition={{ duration: 30, delay: 1, repeat: Infinity, ease: "linear" }}
              className="absolute left-1/4 bottom-1/4 w-96 h-96 border border-amber-600 rounded-full"
            ></motion.div>
          </>
        )}

        {/* Refined circular motifs with amber glow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.07, 0.15, 0.07] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute left-1/2 top-1/2 w-full sm:w-1/2 h-full sm:h-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(217,119,6,0.3) 0%, rgba(217,119,6,0) 75%)'
          }}
        ></motion.div>
      </div>

      {/* Content overlay with luxury spacing and amber accents */}
      <motion.div
        className="relative z-20 flex flex-col items-center justify-center h-full px-4 sm:px-6 md:px-16 lg:px-24"
        style={{
          opacity: textOpacity,
          y: textY
        }}
      >
        <div className="container mx-auto text-center">
          {/* Premium branding element with amber coloring */}
          

          {/* Ultra-premium logo reveal with amber accent */}
          <motion.div
            initial={{ opacity: 0, y: -25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 1.2 : 1.8, delay: isMobile ? 0.2 : 0.4 }}
            className="mb-6 sm:mb-12"
          >
            <div data-aos="fade-down" className="inline-block">
              <div className="text-amber-600 font-serif text-sm tracking-widest uppercase mb-3 sm:mb-5">
                {isMobile ? 'Est. 2005 • True Luxury' : 'Established 2005 • True Luxury • Uncompromised Excellence'}
              </div>
              <div className="flex items-center justify-center space-x-3 sm:space-x-5 mb-3 sm:mb-5">
                <div className="w-8 sm:w-16 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent"></div>
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white tracking-tighter leading-tight">
                  <span className="text-amber-500 font-serif">DEV</span>
                  <span className="font-extralight"> BHOOMI</span>
                  <br />
                  <span className="text-amber-400 font-serif text-2xl md:text-3xl lg:text-4xl tracking-widest block mt-2">
                    GROUP COMPANIES
                  </span>
                </h1>

                <div className="w-8 sm:w-16 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent"></div>
              </div>
              <div className="text-sm sm:text-lg text-white font-extralight tracking-widest uppercase">
                Steel • Automobiles • HOSPITALITY • Infrastructure • Entertainment
              </div>

            </div>
          </motion.div>

          {/* Refined tagline with elegant animation and amber accent */}
          <motion.p
            custom={0}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={textVariants}
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white font-light mb-6 sm:mb-10 max-w-3xl mx-auto"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            <span className="italic font-serif">Driving</span> Extraordinary Spaces for <span className="text-amber-500">Extraordinary Lives</span>
          </motion.p>

          {/* Premium description text - shorter on mobile */}


          {/* Ultra-premium CTA with amber accents - stacked on mobile */}
          <div
            className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6"
            data-aos="fade-up"
            data-aos-delay="700"
          >
            {/* First Button - Slide Effect */}
            <Link to="/business">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: isMobile ? 1.5 : 2.5, duration: isMobile ? 0.6 : 0.9 }}
                className="px-6 sm:px-10 py-3 sm:py-4 bg-amber-500 text-black font-medium tracking-widest uppercase text-xs sm:text-sm transition-all duration-300 group overflow-hidden relative w-full sm:w-auto mb-4 sm:mb-0"
              >
                <span className="relative z-10 group-hover:text-black transition-colors duration-300">Explore Our Ventures</span>
                <span className="absolute inset-0 bg-white z-0 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-600 ease-out"></span>

                <motion.span
                  className="absolute inset-0 bg-white z-0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </motion.button>
            </Link>

            {/* Second Button - Glow Effect */}
            <Link to="/contact">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 0 15px rgba(245, 158, 11, 0.7)",
                  textShadow: "0 0 5px rgba(245, 158, 11, 0.5)"
                }}
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: isMobile ? 1.7 : 2.7, duration: isMobile ? 0.6 : 0.9 }}
                className="px-6 sm:px-10 py-3 sm:py-4 border border-amber-500 text-amber-500 font-medium tracking-widest uppercase text-xs sm:text-sm hover:bg-amber-500 hover:bg-opacity-10 hover:text-white transition-all duration-300 relative overflow-hidden w-full sm:w-auto"
              >
                <span className="relative z-10">Private Consultation</span>
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-400 opacity-0 z-0"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 0.15 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </Link>
          </div>

          {/* Premium award badges with amber accents - hidden on very small screens */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: isMobile ? 2 : 3.2, duration: isMobile ? 1 : 1.8 }}
            className="mt-10 sm:mt-20 hidden xs:flex justify-center space-x-6 sm:space-x-10"
          >
            {['Excellence', 'Premium', 'Luxury'].map((award, index) => (
              <motion.div
                key={award}
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 0.85, y: 0 }}
                transition={{ delay: (isMobile ? 2.2 : 3.4) + index * (isMobile ? 0.15 : 0.25), duration: isMobile ? 0.7 : 1 }}
                whileHover={{ opacity: 1, scale: 1.05 }}
                className="flex flex-col items-center text-xs text-amber-500"
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 border border-amber-500 rounded-full flex items-center justify-center mb-2 sm:mb-3 shadow-amber">
                  <span className="text-amber-500 text-xs sm:text-sm font-serif">{award[0]}</span>
                </div>
                <span className="uppercase tracking-wider font-light text-xs">{award}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

const DEVBhoomiTailwindHero = () => {
  return (
    <>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=Montserrat:wght@200;300;400;500&display=swap');
        
        /* Add responsive font sizing */
        html {
          font-size: 14px;
        }
        
        @media (min-width: 640px) {
          html {
            font-size: 16px;
          }
        }
        
        .font-serif {
          font-family: 'Cormorant Garamond', serif;
        }
        
        body {
          font-family: 'Montserrat', sans-serif;
        }
        
        .shadow-amber {
          box-shadow: 0 0 15px rgba(217, 119, 6, 0.3);
        }
        
        /* Add new breakpoint class for extra small screens */
        @media (min-width: 480px) {
          .xs\:flex {
            display: flex;
          }
        }
      `}</style>

      <HeroSection />
    </>
  );
};

export default DEVBhoomiTailwindHero;